import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import harnessImage from "../assets/images/harness.png";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../utils/analytics";

const stripePromise = loadStripe("pk_live_51TblU4L0sA58YoXrYJGrrAlSkBJCgA3p63X3dXAjOEPeSeUkPwPYI1YF11NLSukbHzTzFyuFnUZLRzpaJRlt08MV00TH4KUDPG");

const formatPhone = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length >= 6) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length >= 3) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  if (digits.length > 0) return `(${digits}`;
  return "";
};

/* ── Payment Form (wires handleSubmit to Pay Now button) ── */
function MobilePaymentForm({
  clientSecret, onSuccess, setPayNowHandler, checkoutData,
  userId, savedCard, product, quantity, selectedCondition,
  selectedVariant, price, appliedCredit = 0
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const checkoutDataRef = React.useRef(checkoutData);
  useEffect(() => { checkoutDataRef.current = checkoutData; }, [checkoutData]);
  const savedCardRef = React.useRef(savedCard);
  useEffect(() => { savedCardRef.current = savedCard; }, [savedCard]);

  const handleSubmit = useCallback(async () => {
    const cd = checkoutDataRef.current;
    const sc = savedCardRef.current;
    if (!stripe || !elements || loading) return;
    setLoading(true);
    setErrorMsg("");

    try {
      const piId = clientSecret.split("_secret_")[0];
      await fetch("https://eleven11-i6v4.onrender.com/api/payments/update-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentIntentId: piId, appliedCredit })
      });
    } catch (err) { console.error(err); }

    const cardElement = elements.getElement(CardElement);
    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      sc ? { payment_method: sc.id } : {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${cd.firstName} ${cd.lastName}`,
            email: cd.email
          }
        }
      }
    );

    setLoading(false);
    if (error) { setErrorMsg(error.message); return; }

    if (
      paymentIntent?.status === "succeeded" ||
      paymentIntent?.status === "processing" ||
      paymentIntent?.status === "requires_capture"
    ) {
      const cartRes = await fetch("https://eleven11-i6v4.onrender.com/api/cart");
      const cartData = await cartRes.json();
      const cartItems = Array.isArray(cartData.items) && cartData.items.length > 0
        ? cartData.items.map(item => ({
            listingId: item.listingId, title: item.title, image: item.image || "",
            partNumber: item.partNumber || "", selectedCondition: item.selectedCondition || "",
            selectedVariant: item.selectedVariant || "", price: Number(item.price), qty: Number(item.qty || 1)
          }))
        : [{
            listingId: product?.listingId || product?._id, title: product?.title,
            image: product?.image || product?.images?.[0] || "", partNumber: product?.partNumber || "",
            selectedCondition, selectedVariant, price: Number(price), qty: Number(quantity || 1)
          }];

      const orderRes = await fetch("https://eleven11-i6v4.onrender.com/api/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id, userId, appliedCredit,
          customer: { firstName: cd.firstName, lastName: cd.lastName, email: cd.email, phone: cd.phone },
          shippingAddress: { address: cd.address, apartment: cd.apartment, city: cd.city, state: cd.state, zip: cd.zip, country: cd.country },
          items: cartItems
        })
      });

      let orderData = {};
      try { orderData = await orderRes.json(); } catch {}
      if (!orderRes.ok) { setErrorMsg(orderData.error || "Order creation failed"); return; }

      await fetch("https://eleven11-i6v4.onrender.com/api/cart/clear", { method: "DELETE" });

      const token = localStorage.getItem("token");
      if (token) {
        await fetch("https://eleven11-i6v4.onrender.com/auth/me/address", {
          method: "PATCH",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            phone: cd.phone, address: cd.address, apartment: cd.apartment,
            city: cd.city, state: cd.state, zip: cd.zip, country: cd.country
          })
        });
      }

      if (!orderData.orderId) { setErrorMsg("Order ID missing"); return; }
      onSuccess(orderData.orderId);
    } else {
      setErrorMsg("Payment not completed. Status: " + paymentIntent?.status);
    }
  }, [stripe, elements, loading, clientSecret, appliedCredit, userId, product, quantity, selectedCondition, selectedVariant, price]);

  useEffect(() => {
    if (setPayNowHandler) setPayNowHandler(() => handleSubmit);
  }, [handleSubmit]);

  return (
    <div>
      {errorMsg && (
        <p style={{ color: "#ef4444", fontSize: 13, fontWeight: 600, margin: "8px 16px 0", textAlign: "center" }}>
          {errorMsg}
        </p>
      )}
    </div>
  );
}

/* ── Main Mobile Checkout ── */
function MobileCheckoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { product, quantity, selectedCondition, selectedVariant, price } = location.state || {};
  const { user, loading } = useAuth();

const [clientSecret, setClientSecret] = useState("");
const [checkoutMode, setCheckoutMode] = useState(null);
const [payNowHandler, setPayNowHandler] = useState(null);
const [savedCard, setSavedCard] = useState(null);
const [appliedCredit, setAppliedCredit] = useState(0);
const [paymentIntentId, setPaymentIntentId] = useState("");
const [cartItems, setCartItems] = useState([]);
const [cartLoading, setCartLoading] = useState(true);

useEffect(() => {
  if (cartLoading || cartItems.length === 0) return;
  trackEvent("checkout_started", {
    items: cartItems.map(item => ({
      title: item.title,
      partNumber: item.partNumber || "",
      condition: item.selectedCondition || "",
      price: item.price,
      qty: item.qty
    })),
    total: cartItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 1), 0)
  });
}, [cartLoading, cartItems]);


const [shippingOpen, setShippingOpen] = useState(false);
const [errors, setErrors] = useState({});
const [qtyError, setQtyError] = useState("");
  const shippingValidateRef = React.useRef(null);

  const [checkoutData, setCheckoutData] = useState({
    firstName: user?.firstName || "", lastName: user?.lastName || "",
    email: user?.email || "", phone: user?.phone || "",
    address: user?.shippingAddress?.address || "", apartment: user?.shippingAddress?.apartment || "",
    city: user?.shippingAddress?.city || "", state: user?.shippingAddress?.state || "",
    zip: user?.shippingAddress?.zip || "", country: user?.shippingAddress?.country || "United States"
  });

  const hasFullAddress = !!(
    checkoutData.firstName && checkoutData.lastName && checkoutData.email &&
    checkoutData.phone && checkoutData.address && checkoutData.city &&
    checkoutData.state && checkoutData.zip
  );

  const subtotal = cartItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0);
  const tax = subtotal * 0.07;
  const totalBeforeCredit = subtotal + tax;
  const total = Math.max(0, totalBeforeCredit - appliedCredit);

  // Fetch cart
  useEffect(() => {
    fetch("https://eleven11-i6v4.onrender.com/api/cart")
      .then(r => r.json())
      .then(data => setCartItems(Array.isArray(data.items) ? data.items : []))
      .catch(() => {})
      .finally(() => setCartLoading(false));
  }, []);

  // Auth mode
  useEffect(() => {
    if (!loading && user && !checkoutMode) setCheckoutMode("account");
  }, [user, loading]);

  // User sync
  const userSynced = React.useRef(false);
  useEffect(() => {
    if (user && !userSynced.current) {
      userSynced.current = true;
      setCheckoutData(prev => ({
        ...prev,
        firstName: prev.firstName || user.firstName || "",
        lastName: prev.lastName || user.lastName || "",
        email: prev.email || user.email || "",
        phone: prev.phone || user.phone || "",
        address: prev.address || user.shippingAddress?.address || "",
        apartment: prev.apartment || user.shippingAddress?.apartment || "",
        city: prev.city || user.shippingAddress?.city || "",
        state: prev.state || user.shippingAddress?.state || "",
        zip: prev.zip || user.shippingAddress?.zip || "",
        country: prev.country || user.shippingAddress?.country || "United States",
      }));
    }
  }, [user]);

  // Payment intent
  useEffect(() => {
    fetch("https://eleven11-i6v4.onrender.com/api/cart")
      .then(r => r.json())
      .then(cartData => {
        const items = Array.isArray(cartData.items) ? cartData.items : [];
        const cartTotal = items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 1), 0);
        const finalPrice = cartTotal > 0 ? cartTotal : Number(price);
        const token = localStorage.getItem("token");
        return fetch("https://eleven11-i6v4.onrender.com/api/payments/create-payment-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ productId: product?.listingId, quantity, selectedCondition, selectedVariant, price: finalPrice })
        });
      })
      .then(r => r.json())
      .then(data => {
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
          setPaymentIntentId(data.clientSecret.split("_secret_")[0]);
          if (data.savedCard) setSavedCard(data.savedCard);
        }
      })
      .catch(err => console.error(err));
  }, []);

  const handleChange = (field, value) => setCheckoutData(prev => ({ ...prev, [field]: value }));

  const validateShipping = () => {
    const required = {
      firstName: "First name", lastName: "Last name", email: "Email",
      phone: "Phone", address: "Street address", city: "City", state: "State", zip: "ZIP code"
    };
    const newErrors = {};
    Object.entries(required).forEach(([field, label]) => {
      if (!checkoutData[field]?.trim()) newErrors[field] = `${label} is required`;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => { shippingValidateRef.current = validateShipping; });

  const handleApplyCredit = async (amount) => {
    setAppliedCredit(amount);
    if (!paymentIntentId) return;
    try {
      await fetch("https://eleven11-i6v4.onrender.com/api/payments/update-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentIntentId, appliedCredit: amount })
      });
    } catch {}
  };

  const removeItem = async (id) => {
    try {
      await fetch(`https://eleven11-i6v4.onrender.com/api/cart/${id}`, { method: "DELETE" });
      setCartItems(prev => prev.filter(item => item._id !== id));
    } catch {}
  };

const updateQty = async (id, newQty) => {
    if (newQty < 1) return;
    try {
      const res = await fetch(`https://eleven11-i6v4.onrender.com/api/cart/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ qty: newQty })
      });
      const data = await res.json();
      if (!res.ok) {
        setQtyError(data.error || "Could not update quantity");
        setTimeout(() => setQtyError(""), 3000);
        return;
      }
      setCartItems(prev => prev.map(item => item._id === id ? { ...item, qty: newQty } : item));
    } catch {}
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#F6F7FB", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 15, color: "#9CA3AF", fontWeight: 600 }}>Loading...</div>
      </div>
    );
  }

  if (!checkoutMode) {
    return (
      <div style={{ minHeight: "100vh", background: "#F6F7FB", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ width: "100%", background: "#fff", borderRadius: 24, padding: "36px 24px", boxShadow: "0 8px 40px rgba(0,0,0,0.08)" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5, margin: "0 0 8px" }}>How would you like to checkout?</h2>
          <p style={{ fontSize: 14, color: "#6B7280", margin: "0 0 28px", lineHeight: "20px" }}>
            Create an account to track orders, save your details, and get faster checkout.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={() => navigate("/auth")} style={{ width: "100%", padding: "16px", borderRadius: 14, border: "none", background: "#0f0f17", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              Login to My Account
            </button>
            <button onClick={() => navigate("/auth")} style={{ width: "100%", padding: "16px", borderRadius: 14, border: "2px solid #E5E7EB", background: "#fff", color: "#0f0f17", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              Create Account
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "4px 0" }}>
              <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
              <span style={{ fontSize: 13, color: "#9CA3AF" }}>or</span>
              <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
            </div>
            <button onClick={() => setCheckoutMode("guest")} style={{ width: "100%", padding: "16px", borderRadius: 14, border: "1px solid #E5E7EB", background: "#F9FAFB", color: "#6B7280", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
              Continue as Guest
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div style={{ minHeight: "100vh", background: "#F6F7FB", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 15, color: "#9CA3AF", fontWeight: 600 }}>Setting up payment...</div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <div style={{ background: "#F6F7FB", minHeight: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, system-ui", paddingBottom: 40 }}>

        {/* Breadcrumb */}
        <div style={{ padding: "16px 16px 8px", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#9ca3af" }}>Shopping Cart</span>
          <span style={{ color: "#9ca3af" }}>›</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#0f0f17" }}>Checkout</span>
        </div>

        <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── 1. ORDER SUMMARY ── */}
          <div style={{ background: "linear-gradient(180deg,#ffffff 0%,#eef2ff 100%)", borderRadius: 22, padding: 18, border: "2px solid #fff", boxShadow: "0 0 30px rgba(120,170,255,0.18), 0 14px 40px rgba(0,0,0,0.08)" }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: "#2F3441", margin: "0 0 14px" }}>Order Summary</h3>
            <div style={{ height: 1, background: "#E6E8EC", marginBottom: 16 }} />

            {/* Cart Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 16 }}>
              {cartLoading ? (
                <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>Loading items...</div>
              ) : cartItems.map(item => (
                <div key={item._id} style={{ display: "grid", gridTemplateColumns: "56px 1fr auto", gap: 12, alignItems: "center" }}>
                  <div style={{ position: "relative" }}>
                    <img src={item.image || harnessImage} alt="part" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }} />
                    <button onClick={() => removeItem(item._id)} style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", border: "none", background: "#f5f6fa", color: "#8b93a7", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#2F3441", marginBottom: 6 }}>{item.title}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", background: "#f7f8fc", borderRadius: 8, border: "1px solid #E3E6EE", padding: "2px 4px", gap: 4 }}>
                        <button onClick={() => updateQty(item._id, item.qty - 1)} style={{ width: 20, height: 20, borderRadius: 5, border: "none", background: "#fff", fontSize: 14, cursor: "pointer" }}>−</button>
                        <span style={{ fontSize: 13, fontWeight: 600, width: 16, textAlign: "center" }}>{item.qty}</span>
                     <button
  onClick={() => item.qty < (item.selectedVariant?.stock ?? Infinity) && updateQty(item._id, item.qty + 1)}
  style={{ width: 20, height: 20, borderRadius: 5, border: "none", background: "#fff", fontSize: 14, cursor: item.qty >= (item.selectedVariant?.stock ?? Infinity) ? "not-allowed" : "pointer", opacity: item.qty >= (item.selectedVariant?.stock ?? Infinity) ? 0.4 : 1 }}
>+</button>
                      </div>
                      {item.selectedCondition && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: item.selectedCondition === "new" ? "#e0f2fe" : "#f3f4f6", color: item.selectedCondition === "new" ? "#0369a1" : "#6b7280" }}>
                          {item.selectedCondition === "new" ? "Brand New" : "Used"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#2F3441" }}>${Number(item.price || 0).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: "#E6E8EC", marginBottom: 14 }} />

            {/* Totals */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14, color: "#4A4F63", marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Subtotal:</span><span>${subtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Shipping:</span><span>Free</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Estimated Tax:</span><span>${tax.toFixed(2)}</span>
              </div>

              {/* Store Credit */}
              {(user?.storeCredit || 0) > 0 && (
                <div style={{ marginTop: 4, padding: "12px 14px", borderRadius: 12, background: "#ecfdf5", border: "1px solid #6ee7b7" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: appliedCredit > 0 ? 8 : 0 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#065f46" }}>Store Credit</div>
                      <div style={{ fontSize: 11, color: "#059669" }}>Available: ${Number(user.storeCredit).toFixed(2)}</div>
                    </div>
                    {appliedCredit > 0 ? (
                      <button onClick={() => handleApplyCredit(0)} style={{ fontSize: 12, fontWeight: 700, color: "#dc2626", background: "none", border: "none", cursor: "pointer" }}>Remove</button>
                    ) : (
                      <button onClick={() => handleApplyCredit(Math.min(user.storeCredit, totalBeforeCredit))} style={{ fontSize: 12, fontWeight: 700, color: "#065f46", background: "#d1fae5", border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}>Apply</button>
                    )}
                  </div>
                  {appliedCredit > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#065f46", fontWeight: 600 }}>
                      <span>Credit Applied</span><span>−${appliedCredit.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ height: 1, background: "#E6E8EC", marginBottom: 14 }} />

            {/* Total */}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 22, fontWeight: 600, color: "#2F3441", marginBottom: 18 }}>
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>

          {/* Qty Error */}
            {qtyError && (
              <div style={{ background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 700, padding: "8px 14px", borderRadius: 10, marginBottom: 12, textAlign: "center" }}>
                ⚠ {qtyError}
              </div>
            )}

            {/* Pay Now */}
            <button
              style={{ width: "100%", padding: "16px", borderRadius: 14,border: "none", background: "linear-gradient(180deg,#5a8efc,#1f57c8)", color: "#fff", fontSize: 16, fontWeight: 600, cursor: cartLoading || cartItems.length === 0 ? "not-allowed" : "pointer", opacity: cartLoading || cartItems.length === 0 ? 0.6 : 1, boxShadow: "0 10px 20px rgba(62,111,227,0.35)" }}
              disabled={cartLoading || cartItems.length === 0}
              onClick={() => {
                const valid = shippingValidateRef.current?.();
                if (!valid) {
                  alert("Please fill out all required shipping details before placing your order.");
                  setShippingOpen(true);
                  return;
                }
                if (typeof payNowHandler === "function") payNowHandler();
              }}
            >
              Pay Now (${total.toFixed(2)})
            </button>

            {/* Footer */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 1, background: "#E6E8EC" }} />
              <span style={{ fontSize: 13, letterSpacing: 2, color: "#9AA1B2", fontWeight: 500 }}>ELEVEN</span>
              <div style={{ flex: 1, height: 1, background: "#E6E8EC" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 14, fontSize: 12, color: "#6F7A8A" }}>
              <span>⊕ OEM Quality Guaranteed</span>
              <span style={{ color: "#9AA1B2" }}>›</span>
              <span>Expert Support Included</span>
            </div>
          </div>

          {/* ── 2. SHIPPING DETAILS ── */}
          <div style={{ background: "linear-gradient(180deg,#ffffff 0%,#f6f8ff 100%)", borderRadius: 22, padding: 20, border: "2px solid #fff", boxShadow: "0 0 30px rgba(120,170,255,0.18), 0 14px 40px rgba(0,0,0,0.08)" }}>
            <button onClick={() => setShippingOpen(!shippingOpen)} style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", padding: 0 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#0f0f17" }}>Shipping Address</span>
              <span style={{ fontSize: 20, color: "#9ca3af", display: "inline-block", transform: shippingOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>⌄</span>
            </button>

            {!shippingOpen && hasFullAddress && (
              <div style={{ marginTop: 12, fontSize: 13, color: "#6B7280", lineHeight: "20px" }}>
                <div style={{ fontWeight: 600, color: "#111", marginBottom: 2 }}>{checkoutData.firstName} {checkoutData.lastName}</div>
                {checkoutData.address}{checkoutData.apartment ? `, ${checkoutData.apartment}` : ""}<br />
                {checkoutData.city}, {checkoutData.state} {checkoutData.zip}
              </div>
            )}
            {!shippingOpen && !hasFullAddress && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#ef4444", fontWeight: 500 }}>⚠ Please fill in shipping details</div>
            )}

            {shippingOpen && (
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div style={labelStyle}>First Name*</div>
                    <input style={{ ...inputStyle, borderColor: errors.firstName ? "#ef4444" : "#D0D5DD" }} placeholder="First Name" value={checkoutData.firstName}
                      onChange={e => { handleChange("firstName", e.target.value); setErrors(p => ({ ...p, firstName: "" })); }} />
                  </div>
                  <div>
                    <div style={labelStyle}>Last Name*</div>
                    <input style={{ ...inputStyle, borderColor: errors.lastName ? "#ef4444" : "#D0D5DD" }} placeholder="Last Name" value={checkoutData.lastName}
                      onChange={e => { handleChange("lastName", e.target.value); setErrors(p => ({ ...p, lastName: "" })); }} />
                  </div>
                </div>

                <div>
                  <div style={labelStyle}>Email*</div>
                  <input style={{ ...inputStyle, borderColor: errors.email ? "#ef4444" : "#D0D5DD" }} placeholder="you@example.com" value={checkoutData.email}
                    onChange={e => { handleChange("email", e.target.value); setErrors(p => ({ ...p, email: "" })); }} />
                </div>

                <div>
                  <div style={labelStyle}>Phone*</div>
                  <input style={{ ...inputStyle, borderColor: errors.phone ? "#ef4444" : "#D0D5DD" }} placeholder="(555) 555-5555" value={checkoutData.phone}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      let f = digits;
                      if (digits.length >= 6) f = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                      else if (digits.length >= 3) f = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                      else if (digits.length > 0) f = `(${digits}`;
                      handleChange("phone", f);
                      setErrors(p => ({ ...p, phone: "" }));
                    }} />
                </div>

                <div>
                  <div style={labelStyle}>Street Address*</div>
                  <input style={{ ...inputStyle, borderColor: errors.address ? "#ef4444" : "#D0D5DD" }} placeholder="1234 Ocean Blvd" value={checkoutData.address}
                    onChange={e => { handleChange("address", e.target.value); setErrors(p => ({ ...p, address: "" })); }} />
                </div>

                <div>
                  <div style={labelStyle}>Apartment / Suite</div>
                  <input style={inputStyle} placeholder="Optional" value={checkoutData.apartment}
                    onChange={e => handleChange("apartment", e.target.value)} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.8fr 1fr", gap: 10 }}>
                  <div>
                    <div style={labelStyle}>City*</div>
                    <input style={{ ...inputStyle, borderColor: errors.city ? "#ef4444" : "#D0D5DD" }} placeholder="City" value={checkoutData.city}
                      onChange={e => { handleChange("city", e.target.value.replace(/\b\w/g, c => c.toUpperCase())); setErrors(p => ({ ...p, city: "" })); }} />
                  </div>
                  <div>
                    <div style={labelStyle}>State*</div>
                    <input style={{ ...inputStyle, borderColor: errors.state ? "#ef4444" : "#D0D5DD" }} placeholder="FL" value={checkoutData.state}
                      onChange={e => { handleChange("state", e.target.value.toUpperCase().slice(0,2)); setErrors(p => ({ ...p, state: "" })); }} />
                  </div>
                  <div>
                    <div style={labelStyle}>ZIP*</div>
                    <input style={{ ...inputStyle, borderColor: errors.zip ? "#ef4444" : "#D0D5DD" }} placeholder="33082" value={checkoutData.zip}
                      onChange={e => { handleChange("zip", e.target.value); setErrors(p => ({ ...p, zip: "" })); }} />
                  </div>
                </div>

                {Object.keys(errors).some(k => errors[k]) && (
                  <div style={{ color: "#ef4444", fontSize: 13, fontWeight: 500 }}>Please fill in all required fields.</div>
                )}

                <button onClick={() => {
                  if (validateShipping()) setShippingOpen(false);
                }} style={{ alignSelf: "flex-start", background: "none", border: "none", fontSize: 13, fontWeight: 600, color: "#635BFF", cursor: "pointer", padding: 0 }}>
                  ← Save & collapse
                </button>
              </div>
            )}
          </div>

          {/* ── 3. PAYMENT METHOD ── */}
          <div style={{ background: "linear-gradient(180deg,#ffffff 0%,#f6f8ff 100%)", borderRadius: 22, padding: 20, border: "2px solid #fff", boxShadow: "0 0 30px rgba(120,170,255,0.18), 0 14px 40px rgba(0,0,0,0.08)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f0f17", marginBottom: 14 }}>Payment Method</div>

            {savedCard ? (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 12, border: "1px solid #E5E7EB", background: "#fff", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <img src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg" alt="card" style={{ width: 38, height: 24, objectFit: "contain" }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>
                        {savedCard.brand.charAt(0).toUpperCase() + savedCard.brand.slice(1)} •••• {savedCard.last4}
                      </div>
                      <div style={{ fontSize: 12, color: "#9CA3AF" }}>Expires {savedCard.expMonth}/{savedCard.expYear}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#10B981", fontWeight: 700 }}>SAVED · Instant Checkout</div>
                </div>
                <button onClick={() => setSavedCard(null)} style={{ width: "100%", padding: "12px", borderRadius: 12, border: "1px solid #E5E7EB", background: "#F9FAFB", fontSize: 13, fontWeight: 600, color: "#6B7280", cursor: "pointer" }}>
                  Use a different card
                </button>
              </div>
            ) : (
              <div style={{ padding: "14px", border: "1px solid #D0D5DD", borderRadius: 12, background: "#fff" }}>
                <CardElement />
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 14, padding: "10px", borderRadius: 10, background: "#F9FAFB", border: "1px solid #E5E7EB" }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 1L10 6H15L11 9.5L12.5 14.5L8 11.5L3.5 14.5L5 9.5L1 6H6L8 1Z" fill="#10B981"/>
              </svg>
              <span style={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>Payments secured by</span>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#635BFF" }}>stripe</span>
            </div>
          </div>

          {/* ── Local Pickup ── */}
          <div style={{ background: "#f9f9fb", borderRadius: 16, padding: "14px 16px", border: "1px solid #eee" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#2F3441" }}>Local Pickup · FACILITY ELEVEN</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#EDEDF0", color: "#9CA3AF" }}>COMING SOON</span>
            </div>
            <p style={{ fontSize: 12, color: "#B0B7C3", margin: 0, lineHeight: "17px" }}>
              Pick up at FACILITY ELEVEN or an Authorized Dealer near you.
            </p>
          </div>

        </div>

        {/* Hidden PaymentForm */}
        <MobilePaymentForm
          clientSecret={clientSecret}
          checkoutData={checkoutData}
          userId={user?.id}
          savedCard={savedCard}
          product={product}
          quantity={quantity}
          selectedCondition={selectedCondition}
          selectedVariant={selectedVariant}
          price={price}
          appliedCredit={appliedCredit}
          onSuccess={(orderId) => navigate(`/invoice/${orderId}`)}
          setPayNowHandler={setPayNowHandler}
        />

      </div>
    </Elements>
  );
}

const labelStyle = { fontSize: 12, color: "#98A1B2", marginBottom: 6 };
const inputStyle = {
  width: "100%", padding: "12px 14px", borderRadius: 10,
  border: "1px solid #D0D5DD", background: "#fff", fontSize: 14,
  outline: "none", boxSizing: "border-box"
};

export default MobileCheckoutInner;